import React, { useState, useEffect } from "react";
import {
  Plus,
  Users,
  Target,
  BookOpen,
  Trash2,
  Calendar,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Bell,
  UserCheck,
  Layers,
  Sparkles,
  BarChart3,
  Archive,
  RotateCcw,
  Pencil,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import { useNavigate } from "react-router-dom";
import { ConfirmActionModal, type ConfirmAction } from "./components/modals";

interface CohortAssignedCourseProgress {
  course_id: number;
  course_title: string;
  assignment_id: number;
  due_date?: string | null;
  is_overdue: boolean;
  members_started: number;
  members_completed: number;
  avg_progress_pct: number;
  avg_quiz_score: number;
  overdue_count: number;
}

interface CohortProgressData {
  cohort_id: number;
  cohort_name: string;
  total_members: number;
  assigned_courses: CohortAssignedCourseProgress[];
  overall_completion_pct: number;
}

interface CohortMember {
  user_id: number;
  name?: string | null;
  email?: string | null;
  role: string;
  department?: string | null;
}

interface Cohort {
  id: number;
  uuid: string;
  organization_id: number;
  name: string;
  description?: string | null;
  status?: string;
  member_count: number;
  members: CohortMember[];
  created_at: string;
}

interface CourseAssignment {
  id: number;
  uuid: string;
  organization_id: number;
  course_id: number;
  course_title?: string | null;
  module_id?: number | null;
  module_title?: string | null;
  cohort_id?: number | null;
  cohort_name?: string | null;
  user_id?: number | null;
  user_name?: string | null;
  user_email?: string | null;
  is_mandatory: boolean;
  passing_threshold_score: number;
  due_date?: string | null;
  is_overdue?: boolean;
  created_at: string;
}

interface SkillExpectation {
  id: number;
  organization_id: number;
  skill_name: string;
  target_proficiency: string;
  department?: string | null;
  created_at: string;
}

interface MemberOption {
  user_id: number;
  name?: string | null;
  email?: string | null;
  department?: string | null;
}

function getMemberDisplayName(m: { name?: string | null; email?: string | null; user_id?: number }): string {
  if (m.name && m.email) return `${m.name} (${m.email})`;
  if (m.name) return m.name;
  if (m.email) return m.email;
  return m.user_id ? `Member #${m.user_id}` : "Member";
}

export function LearningRulesTab({ slug, stats }: { slug: string; stats: any }) {
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState<"cohorts" | "assignments" | "skills">("cohorts");

  // Data states
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [skills, setSkills] = useState<SkillExpectation[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Cohort Modal States
  const [showCreateCohortModal, setShowCreateCohortModal] = useState(false);
  const [newCohortName, setNewCohortName] = useState("");
  const [newCohortDescription, setNewCohortDescription] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [creatingCohort, setCreatingCohort] = useState(false);

  // Manage Cohort Members Modal
  const [managingCohort, setManagingCohort] = useState<Cohort | null>(null);
  const [addMemberIds, setAddMemberIds] = useState<number[]>([]);
  const [updatingCohortMembers, setUpdatingCohortMembers] = useState(false);

  // Edit Cohort Modal
  const [editingCohort, setEditingCohort] = useState<Cohort | null>(null);
  const [editCohortName, setEditCohortName] = useState("");
  const [editCohortDescription, setEditCohortDescription] = useState("");
  const [editCohortStatus, setEditCohortStatus] = useState("active");
  const [updatingCohort, setUpdatingCohort] = useState(false);

  // View Cohort Progress Modal
  const [viewingProgressCohort, setViewingProgressCohort] = useState<Cohort | null>(null);
  const [cohortProgress, setCohortProgress] = useState<CohortProgressData | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  // Cohort Filter State
  const [cohortFilter, setCohortFilter] = useState<"active" | "archived" | "all">("active");

  // Assignment Modal States
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignCourseId, setAssignCourseId] = useState<number | "">("");
  const [assignModuleId, setAssignModuleId] = useState<number | "">("");
  const [assignTargetType, setAssignTargetType] = useState<"cohort" | "user">("cohort");
  const [assignCohortId, setAssignCohortId] = useState<number | "">("");
  const [assignUserId, setAssignUserId] = useState<number | "">("");
  const [assignPricingTierId, setAssignPricingTierId] = useState<number | "">("");
  const [assignMandatory, setAssignMandatory] = useState(true);
  const [assignPassingScore, setAssignPassingScore] = useState(70);
  const [assignDueDate, setAssignDueDate] = useState("");
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  // Selected Course and Cohort Pricing Tier Logic
  const selectedCourse = React.useMemo(() => {
    if (!assignCourseId) return null;
    return stats?.sponsored_courses?.find((c: any) => c.id === Number(assignCourseId)) ?? null;
  }, [stats?.sponsored_courses, assignCourseId]);

  const activeTiers = React.useMemo(() => {
    return (selectedCourse?.org_pricing_tiers || []).filter((t: any) => t.is_active);
  }, [selectedCourse]);

  const selectedCohort = React.useMemo(() => {
    if (!assignCohortId) return null;
    return cohorts.find((c) => c.id === Number(assignCohortId)) ?? null;
  }, [cohorts, assignCohortId]);

  const cohortMemberCount = selectedCohort?.member_count ?? 0;

  const recommendedTier = React.useMemo(() => {
    if (activeTiers.length === 0) return null;
    const paidTiers = activeTiers.filter((t: any) => Number(t.price) > 0);
    const candidateTiers =
      assignTargetType === "cohort" && paidTiers.length > 0 ? paidTiers : activeTiers;
    const sorted = [...candidateTiers].sort((a: any, b: any) => {
      if (a.min_seats !== b.min_seats) return a.min_seats - b.min_seats;
      return (a.max_seats ?? Infinity) - (b.max_seats ?? Infinity);
    });
    if (cohortMemberCount <= 0) return sorted[0];
    const exact = sorted.find(
      (t: any) => cohortMemberCount >= t.min_seats && cohortMemberCount <= (t.max_seats ?? Infinity)
    );
    if (exact) return exact;
    const covering = sorted.find((t: any) => (t.max_seats ?? Infinity) >= cohortMemberCount);
    if (covering) return covering;
    return sorted[sorted.length - 1];
  }, [activeTiers, cohortMemberCount, assignTargetType]);

  React.useEffect(() => {
    if (recommendedTier) {
      setAssignPricingTierId(recommendedTier.id);
    } else if (activeTiers.length > 0) {
      setAssignPricingTierId(activeTiers[0].id);
    } else {
      setAssignPricingTierId("");
    }
  }, [assignCourseId, recommendedTier]);

  const selectedTier = React.useMemo(() => {
    return activeTiers.find((t: any) => t.id === Number(assignPricingTierId)) ?? recommendedTier ?? null;
  }, [activeTiers, assignPricingTierId, recommendedTier]);

  const isTierPurchased = (tierId?: number | null) => {
    if (!tierId) return false;
    return selectedCourse?.purchased_tier_ids?.includes(tierId) || false;
  };

  const requiresCohortPayment =
    assignTargetType === "cohort" &&
    selectedTier &&
    Number(selectedTier.price) > 0 &&
    !isTierPurchased(selectedTier.id);

  // Skill Expectation Modal States
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [newSkillName, setNewSkillName] = useState("");
  const [newSkillProficiency, setNewSkillProficiency] = useState("Intermediate");
  const [newSkillDept, setNewSkillDept] = useState("All");
  const [creatingSkill, setCreatingSkill] = useState(false);

  const loadAll = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const [cohRes, assRes, skRes, memRes] = await Promise.all([
        api.listCohortsApiV1B2bSlugCohortsGet({
          path: { slug },
          query: { include_archived: true },
        }),
        api.listAssignmentsApiV1B2bSlugAssignmentsGet({
          path: { slug },
        }),
        api.listSkillExpectationsApiV1B2bSlugSkillsGet({
          path: { slug },
        }),
        api.listOrgMembersApiV1B2bSlugMembersGet({
          path: { slug },
        }),
      ]);

      if (!cohRes.error && cohRes.data) {
        setCohorts((cohRes.data.cohorts || []) as unknown as Cohort[]);
      }
      if (!assRes.error && assRes.data) {
        setAssignments((assRes.data.assignments || []) as unknown as CourseAssignment[]);
      }
      if (!skRes.error && skRes.data) {
        setSkills((skRes.data.skills || []) as unknown as SkillExpectation[]);
      }
      if (!memRes.error && memRes.data) {
        setMembers((memRes.data.members || []) as unknown as MemberOption[]);
      }
    } catch (err) {
      console.error("Failed to load learning rules data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [slug]);

  // Handle Create Cohort
  const handleCreateCohort = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCohortName.trim()) return;
    try {
      setCreatingCohort(true);
      const { error } = await api.createCohortApiV1B2bSlugCohortsPost({
        path: { slug },
        body: {
          name: newCohortName.trim(),
          description: newCohortDescription.trim() || undefined,
          member_ids: selectedMemberIds,
        },
      });
      if (!error) {
        setShowCreateCohortModal(false);
        setNewCohortName("");
        setNewCohortDescription("");
        setSelectedMemberIds([]);
        setStatusMessage({ text: "Cohort created successfully!", type: "success" });
        await loadAll();
      } else {
        setStatusMessage({ text: "Failed to create cohort.", type: "error" });
      }
    } catch (err) {
      setStatusMessage({ text: "Failed to create cohort.", type: "error" });
    } finally {
      setCreatingCohort(false);
    }
  };

  // Handle Delete Cohort
  const handleDeleteCohort = (cohortId: number) => {
    const cohort = cohorts.find((item) => item.id === cohortId);
    setConfirmAction({
      title: "Delete cohort?",
      description: `Delete ${cohort?.name || "this cohort"}? Its cohort membership and assignment grouping will be removed.`,
      confirmLabel: "Delete cohort",
      onConfirm: async () => {
        try {
          const { error } = await api.deleteCohortApiV1B2bSlugCohortsCohortIdDelete({
            path: { slug, cohort_id: cohortId },
          });
          if (!error) {
            setStatusMessage({ text: "Cohort deleted successfully.", type: "success" });
            await loadAll();
          } else {
            setStatusMessage({ text: "Failed to delete cohort.", type: "error" });
          }
        } catch (err) {
          setStatusMessage({ text: "Failed to delete cohort.", type: "error" });
        }
      },
    });
  };

  // Handle Add Members to Cohort
  const handleAddCohortMembers = async () => {
    if (!managingCohort || addMemberIds.length === 0) return;
    try {
      setUpdatingCohortMembers(true);
      const { data, error } = await api.addCohortMembersApiV1B2bSlugCohortsCohortIdMembersPost({
        path: { slug, cohort_id: managingCohort.id },
        body: { user_ids: addMemberIds },
      });
      if (!error && data) {
        setManagingCohort(data as unknown as Cohort);
        setAddMemberIds([]);
        setStatusMessage({ text: "Members added to cohort!", type: "success" });
        await loadAll();
      } else {
        setStatusMessage({ text: "Failed to add members.", type: "error" });
      }
    } catch (err) {
      setStatusMessage({ text: "Failed to add members.", type: "error" });
    } finally {
      setUpdatingCohortMembers(false);
    }
  };

  // Handle Remove Cohort Member
  const handleRemoveCohortMember = async (cohortId: number, userId: number) => {
    try {
      const { error } = await api.removeCohortMemberApiV1B2bSlugCohortsCohortIdMembersUserIdDelete({
        path: { slug, cohort_id: cohortId, user_id: userId },
      });
      if (!error) {
        if (managingCohort) {
          setManagingCohort((prev) =>
            prev ? { ...prev, members: prev.members.filter((m) => m.user_id !== userId), member_count: prev.member_count - 1 } : null
          );
        }
        await loadAll();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Handle View Cohort Progress
  const handleViewProgress = async (c: Cohort) => {
    setViewingProgressCohort(c);
    setCohortProgress(null);
    setLoadingProgress(true);
    try {
      const { data, error } = await api.getCohortProgressApiV1B2bSlugCohortsCohortIdProgressGet({
        path: { slug, cohort_id: c.id },
      });
      if (!error && data) {
        setCohortProgress(data as unknown as CohortProgressData);
      }
    } catch (err) {
      console.error("Failed to load cohort progress", err);
    } finally {
      setLoadingProgress(false);
    }
  };

  // Handle Update Cohort Status (archive, activate, complete)
  const handleUpdateCohortStatus = async (cohortId: number, newStatus: string) => {
    try {
      const { error } = await api.updateCohortApiV1B2bSlugCohortsCohortIdPatch({
        path: { slug, cohort_id: cohortId },
        body: { status: newStatus as any },
      });
      if (!error) {
        setStatusMessage({
          text: newStatus === "archived" ? "Cohort archived." : newStatus === "completed" ? "Cohort marked complete." : "Cohort reactivated.",
          type: "success",
        });
        await loadAll();
      }
    } catch (err) {
      console.error("Failed to update cohort status", err);
    }
  };

  // Handle Cohort Details Edit (name, description, status)
  const handleUpdateCohort = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCohort || !editCohortName.trim()) return;
    try {
      setUpdatingCohort(true);
      const { error } = await api.updateCohortApiV1B2bSlugCohortsCohortIdPatch({
        path: { slug, cohort_id: editingCohort.id },
        body: {
          name: editCohortName.trim(),
          description: editCohortDescription.trim() || undefined,
          status: editCohortStatus as any,
        },
      });
      if (!error) {
        setEditingCohort(null);
        setStatusMessage({ text: "Cohort updated successfully!", type: "success" });
        await loadAll();
      } else {
        const err = error as any;
        setStatusMessage({ text: err?.detail || "Failed to update cohort.", type: "error" });
      }
    } catch (err) {
      setStatusMessage({ text: "Failed to update cohort.", type: "error" });
    } finally {
      setUpdatingCohort(false);
    }
  };

  // Handle Create Assignment
  const handleCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignCourseId) return;
    if (assignTargetType === "cohort" && !assignCohortId) return;
    if (assignTargetType === "user" && !assignUserId) return;

    if (requiresCohortPayment && selectedTier) {
      setShowAssignModal(false);
      navigate(
        `/checkout/tier/${selectedTier.id}?org=${slug}&course=${assignCourseId}`
      );
      return;
    }

    try {
      setCreatingAssignment(true);
      const { error } = await api.assignCourseApiV1B2bSlugAssignmentsPost({
        path: { slug },
        body: {
          course_id: Number(assignCourseId),
          module_id: assignModuleId ? Number(assignModuleId) : undefined,
          cohort_id: assignTargetType === "cohort" ? Number(assignCohortId) : undefined,
          user_id: assignTargetType === "user" ? Number(assignUserId) : undefined,
          pricing_tier_id: assignTargetType === "cohort" && selectedTier ? Number(selectedTier.id) : undefined,
          is_mandatory: assignMandatory,
          passing_threshold_score: assignPassingScore,
          due_date: assignDueDate ? new Date(assignDueDate).toISOString() : undefined,
        },
      });

      if (!error) {
        setShowAssignModal(false);
        setAssignCourseId("");
        setAssignModuleId("");
        setAssignCohortId("");
        setAssignUserId("");
        setAssignDueDate("");
        setAssignPricingTierId("");
        setStatusMessage({ text: "Curriculum assigned successfully!", type: "success" });
        await loadAll();
      } else {
        const errObj = error as any;
        const msg = errObj?.detail || errObj?.error || "Failed to create assignment.";
        setStatusMessage({ text: msg, type: "error" });
      }
    } catch (err: any) {
      setStatusMessage({ text: err?.message || "Failed to create assignment.", type: "error" });
    } finally {
      setCreatingAssignment(false);
    }
  };

  // Handle Delete Assignment
  const handleDeleteAssignment = (assignmentId: number) => {
    const assignment = assignments.find((item) => item.id === assignmentId);
    setConfirmAction({
      title: "Revoke course assignment?",
      description: `Revoke ${assignment?.course_title || "this course assignment"}? Learners will no longer have this assignment in their learning plan.`,
      confirmLabel: "Revoke assignment",
      onConfirm: async () => {
        try {
          const { error } = await api.deleteAssignmentApiV1B2bSlugAssignmentsAssignmentIdDelete({
            path: { slug, assignment_id: assignmentId },
          });
          if (!error) {
            setStatusMessage({ text: "Assignment revoked.", type: "success" });
            await loadAll();
          } else {
            setStatusMessage({ text: "Failed to revoke assignment.", type: "error" });
          }
        } catch (err) {
          setStatusMessage({ text: "Failed to revoke assignment.", type: "error" });
        }
      },
    });
  };

  // Handle Send Reminders
  const handleSendReminders = async () => {
    try {
      setSendingReminders(true);
      const { error } = await api.sendAssignmentRemindersApiV1B2bSlugAssignmentsRemindersPost({
        path: { slug },
      });
      if (!error) {
        setStatusMessage({ text: "Due date email reminders dispatched to pending learners!", type: "success" });
      } else {
        setStatusMessage({ text: "Failed to dispatch reminders.", type: "error" });
      }
    } catch (err) {
      setStatusMessage({ text: "Failed to dispatch reminders.", type: "error" });
    } finally {
      setSendingReminders(false);
    }
  };

  // Handle Create Skill Expectation
  const handleCreateSkill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;
    try {
      setCreatingSkill(true);
      const { error } = await api.createSkillExpectationApiV1B2bSlugSkillsPost({
        path: { slug },
        body: {
          skill_name: newSkillName.trim(),
          target_proficiency: newSkillProficiency,
          department: newSkillDept === "All" ? undefined : newSkillDept,
        },
      });
      if (!error) {
        setShowSkillModal(false);
        setNewSkillName("");
        setNewSkillProficiency("Intermediate");
        setNewSkillDept("All");
        setStatusMessage({ text: "Skill expectation added!", type: "success" });
        await loadAll();
      } else {
        setStatusMessage({ text: "Failed to add skill expectation.", type: "error" });
      }
    } catch (err) {
      setStatusMessage({ text: "Failed to add skill expectation.", type: "error" });
    } finally {
      setCreatingSkill(false);
    }
  };

  // Handle Delete Skill
  const handleDeleteSkill = async (skillId: number) => {
    try {
      const { error } = await api.deleteSkillExpectationApiV1B2bSlugSkillsSkillIdDelete({
        path: { slug, skill_id: skillId },
      });
      if (!error) {
        setStatusMessage({ text: "Skill expectation deleted.", type: "success" });
        await loadAll();
      } else {
        setStatusMessage({ text: "Failed to delete skill expectation.", type: "error" });
      }
    } catch (err) {
      setStatusMessage({ text: "Failed to delete skill expectation.", type: "error" });
    }
  };

  return (
    <div className="space-y-6">
      <ConfirmActionModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      {/* Toast Feedback */}
      {statusMessage && (
        <div
          className={`p-4 rounded-2xl text-xs flex items-center justify-between gap-3 shadow-sm ${
            statusMessage.type === "success"
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : "bg-rose-50 border border-rose-200 text-rose-800"
          }`}
        >
          <div className="flex items-center gap-2">
            {statusMessage.type === "success" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-rose-600" />}
            {statusMessage.text}
          </div>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-slate-700">
            ✕
          </button>
        </div>
      )}

      {/* Sub-tab Switcher */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div className="flex items-center gap-6">
          <button
            onClick={() => setActiveSubTab("cohorts")}
            className={`text-xs sm:text-sm font-bold pb-2 flex items-center gap-2 border-b-2 transition ${
              activeSubTab === "cohorts" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Users className="w-4 h-4" />
            Cohorts ({cohorts.length})
          </button>
          <button
            onClick={() => setActiveSubTab("assignments")}
            className={`text-xs sm:text-sm font-bold pb-2 flex items-center gap-2 border-b-2 transition ${
              activeSubTab === "assignments" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Course Assignments ({assignments.length})
          </button>
          <button
            onClick={() => setActiveSubTab("skills")}
            className={`text-xs sm:text-sm font-bold pb-2 flex items-center gap-2 border-b-2 transition ${
              activeSubTab === "skills" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Target className="w-4 h-4" />
            Skill Expectations ({skills.length})
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* SUB-TAB 1: COHORTS                                            */}
      {/* ------------------------------------------------------------- */}
      {activeSubTab === "cohorts" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
            <div>
              <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Employee Cohorts & Teams
              </h3>
              <p className="text-xs text-slate-500 max-w-xl">
                Group employees into cohorts (e.g. Engineering Onboarding, Senior Leadership, Data Team) for bulk curriculum assignment and tracking.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center bg-slate-100 p-1 rounded-xl text-xs font-semibold">
                <button
                  onClick={() => setCohortFilter("active")}
                  className={`px-3 py-1.5 rounded-lg transition ${
                    cohortFilter === "active" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Active ({cohorts.filter((c) => (c.status || "active") === "active").length})
                </button>
                <button
                  onClick={() => setCohortFilter("archived")}
                  className={`px-3 py-1.5 rounded-lg transition ${
                    cohortFilter === "archived" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Archived ({cohorts.filter((c) => c.status === "archived").length})
                </button>
              </div>
              <button
                onClick={() => setShowCreateCohortModal(true)}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-sm transition flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
                New Cohort
              </button>
            </div>
          </div>

          {(() => {
            const displayedCohorts = cohorts.filter((c) => {
              if (cohortFilter === "active") return (c.status || "active") !== "archived";
              if (cohortFilter === "archived") return c.status === "archived";
              return true;
            });

            if (displayedCohorts.length === 0) {
              return (
                <div className="py-12 text-center text-slate-500 bg-white border border-slate-200 rounded-3xl shadow-sm">
                  <Users className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm font-semibold text-slate-700">
                    {cohortFilter === "archived" ? "No archived cohorts." : "No active cohorts defined yet."}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {cohortFilter === "archived"
                      ? "Archived cohorts will appear here."
                      : "Create cohorts to group your team for targeted learning assignments."}
                  </p>
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayedCohorts.map((c) => {
                  const isArchived = c.status === "archived";
                  const isCompleted = c.status === "completed";
                  return (
                    <div
                      key={c.id}
                      className={`border rounded-2xl p-5 flex flex-col justify-between shadow-sm transition ${
                        isArchived
                          ? "bg-slate-50/80 border-slate-300 opacity-75"
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="px-2.5 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-bold uppercase rounded-md">
                              {c.member_count} Members
                            </span>
                            {isArchived ? (
                              <span className="px-2 py-0.5 bg-slate-200 border border-slate-300 text-slate-600 text-[10px] font-bold uppercase rounded-md">
                                Archived
                              </span>
                            ) : isCompleted ? (
                              <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase rounded-md">
                                Completed
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingCohort(c);
                                setEditCohortName(c.name);
                                setEditCohortDescription(c.description || "");
                                setEditCohortStatus(c.status || "active");
                              }}
                              className="text-slate-400 hover:text-indigo-600 p-1 transition"
                              title="Edit Cohort"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {isArchived ? (
                              <button
                                onClick={() => handleUpdateCohortStatus(c.id, "active")}
                                className="text-slate-400 hover:text-indigo-600 p-1 transition"
                                title="Reactivate Cohort"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateCohortStatus(c.id, "archived")}
                                className="text-slate-400 hover:text-amber-600 p-1 transition"
                                title="Archive Cohort"
                              >
                                <Archive className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteCohort(c.id)}
                              className="text-slate-400 hover:text-rose-600 p-1 transition"
                              title="Delete Cohort"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <h4 className="text-sm font-bold text-slate-900">{c.name}</h4>
                        {c.description && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{c.description}</p>}

                        {/* Member Avatars / List Preview */}
                        <div className="mt-4 flex flex-wrap gap-1">
                          {c.members.slice(0, 5).map((m) => (
                            <span key={m.user_id} className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[10px] rounded-md">
                              {getMemberDisplayName(m)}
                            </span>
                          ))}
                          {c.members.length > 5 && (
                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] rounded-md">
                              +{c.members.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <button
                          onClick={() => handleViewProgress(c)}
                          className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1"
                        >
                          <BarChart3 className="w-3.5 h-3.5" /> Progress
                        </button>
                        <button
                          onClick={() => setManagingCohort(c)}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                        >
                          <UserCheck className="w-3.5 h-3.5" /> Members
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* SUB-TAB 2: COURSE ASSIGNMENTS & RULES                         */}
      {/* ------------------------------------------------------------- */}
      {activeSubTab === "assignments" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
            <div>
              <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-600" />
                Granular Curriculum Assignments & Rules
              </h3>
              <p className="text-xs text-slate-500 max-w-xl">
                Assign required or optional courses and specific modules to cohorts or individual learners with minimum passing threshold scores and due dates.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSendReminders}
                disabled={sendingReminders || assignments.length === 0}
                className="px-3.5 py-2.5 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl flex items-center gap-2 border border-slate-200 shadow-sm transition disabled:opacity-50"
              >
                <Bell className="w-3.5 h-3.5 text-amber-500" />
                {sendingReminders ? "Dispatching..." : "Send Reminders"}
              </button>
              <button
                onClick={() => setShowAssignModal(true)}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-sm transition flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
                Assign Curriculum
              </button>
            </div>
          </div>

          {assignments.length === 0 ? (
            <div className="py-12 text-center text-slate-500 bg-white border border-slate-200 rounded-3xl shadow-sm">
              <BookOpen className="w-10 h-10 mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-semibold text-slate-700">No curriculum assigned yet.</p>
              <p className="text-xs text-slate-500 mt-1">Assign courses or modules to cohorts or employees to enforce learning compliance.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3.5">Curriculum / Track</th>
                    <th className="px-5 py-3.5">Assigned Target</th>
                    <th className="px-5 py-3.5">Scope</th>
                    <th className="px-5 py-3.5">Passing Score</th>
                    <th className="px-5 py-3.5">Due Date</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {assignments.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50/80 transition">
                      <td className="px-5 py-3.5">
                        <div className="font-bold text-slate-900">{a.course_title || `Course #${a.course_id}`}</div>
                        {a.is_mandatory ? (
                          <span className="inline-block mt-0.5 px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold uppercase rounded">
                            Mandatory
                          </span>
                        ) : (
                          <span className="inline-block mt-0.5 px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-bold uppercase rounded">
                            Optional
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {a.cohort_name ? (
                          <span className="px-2.5 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5">
                            <Users className="w-3 h-3" /> Cohort: {a.cohort_name}
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 bg-slate-100 border border-slate-200 text-slate-800 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5">
                            <UserCheck className="w-3 h-3 text-emerald-600" /> {a.user_name || a.user_email || `User #${a.user_id}`}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {a.module_title ? (
                          <span className="text-slate-700 font-medium">Module: {a.module_title}</span>
                        ) : (
                          <span className="text-slate-400 italic">Entire Course</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 font-bold rounded">
                          {a.passing_threshold_score}%
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-500">
                        {a.due_date ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={a.is_overdue ? "inline-flex items-center gap-1 text-rose-600 font-medium" : "inline-flex items-center gap-1 text-amber-700 font-medium"}>
                              <Calendar className="w-3 h-3" /> {new Date(a.due_date).toLocaleDateString()}
                            </span>
                            {a.is_overdue && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-rose-100 text-rose-700 border border-rose-200 uppercase tracking-wider">
                                Overdue
                              </span>
                            )}
                          </div>
                        ) : (
                          "No deadline"
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={() => handleDeleteAssignment(a.id)}
                          className="text-slate-400 hover:text-rose-600 p-1.5 rounded transition"
                          title="Revoke Assignment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* SUB-TAB 3: SKILL EXPECTATIONS                                 */}
      {/* ------------------------------------------------------------- */}
      {activeSubTab === "skills" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
            <div>
              <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                Department Skill Expectations & Proficiency Targets
              </h3>
              <p className="text-xs text-slate-500 max-w-xl">
                Define corporate skill targets across departments to benchmark team capabilities and trigger automated remediation.
              </p>
            </div>
            <button
              onClick={() => setShowSkillModal(true)}
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-sm transition flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              Add Expectation
            </button>
          </div>

          {skills.length === 0 ? (
            <div className="py-12 text-center text-slate-500 bg-white border border-slate-200 rounded-3xl shadow-sm">
              <Target className="w-10 h-10 mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-semibold text-slate-700">No skill expectations defined yet.</p>
              <p className="text-xs text-slate-500 mt-1">Set benchmark proficiency targets by department to evaluate workforce skill gaps.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {skills.map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start justify-between shadow-sm hover:border-slate-300 transition">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-indigo-700 text-[10px] font-bold uppercase rounded">
                        {s.department || "Company-wide"}
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-slate-900">{s.skill_name}</h4>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xs text-slate-500">Target Proficiency:</span>
                      <span className="px-2.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold rounded-lg">
                        {s.target_proficiency}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteSkill(s.id)} className="text-slate-400 hover:text-rose-600 p-1 transition">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: CREATE COHORT                                          */}
      {/* ------------------------------------------------------------- */}
      {showCreateCohortModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-600" /> Create Team Cohort
              </h3>
              <button onClick={() => setShowCreateCohortModal(false)} className="text-slate-400 hover:text-slate-700">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateCohort} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Cohort Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Q3 Engineering Onboarding"
                  value={newCohortName}
                  onChange={(e) => setNewCohortName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Description (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Cohort goals and focus area..."
                  value={newCohortDescription}
                  onChange={(e) => setNewCohortDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 resize-none"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1.5">Select Initial Members ({selectedMemberIds.length} selected)</label>
                <div className="max-h-40 overflow-y-auto space-y-1.5 border border-slate-200 rounded-xl p-2 bg-slate-50">
                  {members.length === 0 ? (
                    <div className="py-4 text-center text-slate-400 text-xs">No organization members found.</div>
                  ) : (
                    members.map((m) => (
                      <label key={m.user_id} className="flex items-center gap-2 p-1.5 hover:bg-white rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(m.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedMemberIds((prev) => [...prev, m.user_id]);
                            else setSelectedMemberIds((prev) => prev.filter((id) => id !== m.user_id));
                          }}
                          className="rounded text-indigo-600 bg-white border-slate-300 focus:ring-indigo-500"
                        />
                        <span className="text-slate-800">{getMemberDisplayName(m)}</span>
                        {m.department && <span className="text-slate-400 text-[10px]">({m.department})</span>}
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateCohortModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingCohort}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm disabled:opacity-50"
                >
                  {creatingCohort ? "Creating..." : "Create Cohort"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: MANAGE COHORT MEMBERS                                  */}
      {/* ------------------------------------------------------------- */}
      {managingCohort && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-indigo-600" /> Manage: {managingCohort.name}
                </h3>
                <p className="text-[11px] text-slate-500">{managingCohort.members.length} active members</p>
              </div>
              <button onClick={() => setManagingCohort(null)} className="text-slate-400 hover:text-slate-700">
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Current Members List */}
              <div>
                <span className="block font-semibold text-slate-700 mb-2">Current Members</span>
                <div className="max-h-44 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-2 bg-slate-50">
                  {managingCohort.members.length === 0 ? (
                    <div className="py-4 text-center text-slate-400">No members in this cohort.</div>
                  ) : (
                    managingCohort.members.map((m) => (
                      <div key={m.user_id} className="flex items-center justify-between p-1.5 hover:bg-white rounded-lg">
                        <span className="text-slate-800 font-medium">{getMemberDisplayName(m)}</span>
                        <button
                          onClick={() => handleRemoveCohortMember(managingCohort.id, m.user_id)}
                          className="text-slate-400 hover:text-rose-600 text-[11px]"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Add New Members */}
              <div>
                <span className="block font-semibold text-slate-700 mb-2">Add Employees to Cohort</span>
                <div className="max-h-36 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-2 bg-slate-50">
                  {(() => {
                    const availableMembers = members.filter(
                      (m) => !managingCohort.members.some((cm) => cm.user_id === m.user_id)
                    );
                    if (availableMembers.length === 0) {
                      return (
                        <div className="py-4 text-center text-slate-400 text-xs">
                          {members.length === 0
                            ? "No organization members available to add."
                            : "All organization members are already in this cohort."}
                        </div>
                      );
                    }
                    return availableMembers.map((m) => (
                      <label key={m.user_id} className="flex items-center gap-2 p-1.5 hover:bg-white rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addMemberIds.includes(m.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) setAddMemberIds((prev) => [...prev, m.user_id]);
                            else setAddMemberIds((prev) => prev.filter((id) => id !== m.user_id));
                          }}
                          className="rounded text-indigo-600 bg-white border-slate-300 focus:ring-indigo-500"
                        />
                        <span className="text-slate-800">{getMemberDisplayName(m)}</span>
                      </label>
                    ));
                  })()}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setManagingCohort(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
                >
                  Done
                </button>
                <button
                  type="button"
                  disabled={updatingCohortMembers || addMemberIds.length === 0}
                  onClick={handleAddCohortMembers}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm disabled:opacity-50"
                >
                  {updatingCohortMembers ? "Adding..." : `Add ${addMemberIds.length} Members`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: EDIT COHORT                                            */}
      {/* ------------------------------------------------------------- */}
      {editingCohort && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-indigo-600" /> Edit Cohort
              </h3>
              <button
                onClick={() => setEditingCohort(null)}
                className="text-slate-400 hover:text-slate-700 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateCohort} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Cohort Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editCohortName}
                  onChange={(e) => setEditCohortName(e.target.value)}
                  className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={editCohortDescription}
                  onChange={(e) => setEditCohortDescription(e.target.value)}
                  placeholder="e.g. Q3 New Hire engineering batch"
                  className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Status
                </label>
                <select
                  value={editCohortStatus}
                  onChange={(e) => setEditCohortStatus(e.target.value)}
                  className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingCohort(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingCohort || !editCohortName.trim()}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm text-xs disabled:opacity-50"
                >
                  {updatingCohort ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: VIEW COHORT PROGRESS                                   */}
      {/* ------------------------------------------------------------- */}
      {viewingProgressCohort && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" /> Progress: {viewingProgressCohort.name}
                </h3>
                <p className="text-[11px] text-slate-500">
                  {cohortProgress ? `${cohortProgress.total_members} members enrolled` : "Loading progress..."}
                </p>
              </div>
              <button
                onClick={() => {
                  setViewingProgressCohort(null);
                  setCohortProgress(null);
                }}
                className="text-slate-400 hover:text-slate-700 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {loadingProgress ? (
              <div className="py-12 text-center text-slate-500">
                <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs">Calculating cohort completion metrics...</p>
              </div>
            ) : cohortProgress ? (
              <div className="space-y-6">
                {/* Rollup KPI Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-purple-50/60 border border-purple-100 rounded-2xl p-4">
                    <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block mb-1">
                      Completion Rate
                    </span>
                    <div className="text-2xl font-black text-purple-900">
                      {cohortProgress.overall_completion_pct}%
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div
                        className="bg-purple-600 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(cohortProgress.overall_completion_pct, 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Cohort Size
                    </span>
                    <div className="text-2xl font-black text-slate-800">
                      {cohortProgress.total_members}
                    </div>
                    <span className="text-[11px] text-slate-400 mt-1 block">Learners in group</span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Assigned Tracks
                    </span>
                    <div className="text-2xl font-black text-slate-800">
                      {cohortProgress.assigned_courses.length}
                    </div>
                    <span className="text-[11px] text-slate-400 mt-1 block">Courses assigned</span>
                  </div>
                </div>

                {/* Assigned Course Rollup List */}
                <div>
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                    Course Breakdown
                  </h4>
                  {cohortProgress.assigned_courses.length === 0 ? (
                    <div className="py-8 text-center bg-slate-50 border border-slate-200 rounded-2xl text-slate-500">
                      <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs font-medium text-slate-600">No courses assigned to this cohort yet.</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Assign a course from the Assignments tab.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {cohortProgress.assigned_courses.map((course) => (
                        <div
                          key={course.assignment_id}
                          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h5 className="text-sm font-bold text-slate-900">{course.course_title}</h5>
                              <div className="flex items-center gap-2 mt-0.5">
                                {course.due_date ? (
                                  <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> Due {new Date(course.due_date).toLocaleDateString()}
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-slate-400">No deadline</span>
                                )}
                                {course.is_overdue && (
                                  <span className="px-1.5 py-0.2 text-[10px] font-bold rounded bg-rose-100 text-rose-700 border border-rose-200 uppercase">
                                    Overdue
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-extrabold text-slate-800">
                                {course.avg_progress_pct}% avg progress
                              </span>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(course.avg_progress_pct, 100)}%` }}
                            />
                          </div>

                          {/* Metric pills */}
                          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100 flex-wrap gap-2">
                            <span className="text-slate-600">
                              <strong>{course.members_started}</strong> / {cohortProgress.total_members} Started
                            </span>
                            <span className="text-slate-600">
                              <strong>{course.members_completed}</strong> / {cohortProgress.total_members} Completed
                            </span>
                            <span className="text-slate-600">
                              Avg Quiz: <strong>{course.avg_quiz_score}%</strong>
                            </span>
                            {course.overdue_count > 0 && (
                              <span className="text-rose-600 font-semibold">
                                {course.overdue_count} Overdue
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: ASSIGN CURRICULUM                                      */}
      {/* ------------------------------------------------------------- */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-600" /> Assign Learning Track
              </h3>
              <button onClick={() => setShowAssignModal(false)} className="text-slate-400 hover:text-slate-700">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateAssignment} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Select Course *</label>
                <select
                  required
                  value={assignCourseId}
                  onChange={(e) => setAssignCourseId(e.target.value ? Number(e.target.value) : "")}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="">-- Choose Course --</option>
                  {stats.sponsored_courses?.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Assign Target *</label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setAssignTargetType("cohort")}
                    className={`py-2 text-center rounded-xl border font-semibold transition ${
                      assignTargetType === "cohort"
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm"
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    Cohort / Team
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssignTargetType("user")}
                    className={`py-2 text-center rounded-xl border font-semibold transition ${
                      assignTargetType === "user"
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm"
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    Individual Learner
                  </button>
                </div>

                {assignTargetType === "cohort" ? (
                  <select
                    required
                    value={assignCohortId}
                    onChange={(e) => setAssignCohortId(e.target.value ? Number(e.target.value) : "")}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">-- Select Cohort --</option>
                    {cohorts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.member_count} members)
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    required
                    value={assignUserId}
                    onChange={(e) => setAssignUserId(e.target.value ? Number(e.target.value) : "")}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">-- Select Employee --</option>
                    {members.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {getMemberDisplayName(m)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Cohort Pricing & Payment Tier Selector */}
              {assignTargetType === "cohort" && activeTiers.length > 0 && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-800">License / Pricing Tier</span>
                    {selectedTier && isTierPurchased(selectedTier.id) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        Licensed / Purchased
                      </span>
                    ) : selectedTier && Number(selectedTier.price) > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                        <AlertCircle className="w-3 h-3 text-amber-600" />
                        Payment Required
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                        Free Tier
                      </span>
                    )}
                  </div>

                  <select
                    value={assignPricingTierId}
                    onChange={(e) => setAssignPricingTierId(e.target.value ? Number(e.target.value) : "")}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-indigo-500"
                  >
                    {activeTiers.map((t: any) => (
                      <option key={t.id} value={t.id}>
                        {t.name} — {Number(t.price) > 0 ? `$${Number(t.price).toFixed(2)}` : "Free"} (
                        {t.max_seats ? `${t.min_seats}-${t.max_seats} seats` : `${t.min_seats}+ seats`})
                        {isTierPurchased(t.id) ? " [Purchased]" : ""}
                      </option>
                    ))}
                  </select>

                  {requiresCohortPayment && selectedTier && (
                    <div className="p-2.5 bg-amber-50/80 border border-amber-200 rounded-xl flex items-start gap-2 text-[11px] text-amber-900">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        This course requires an organization seat license (<strong>${Number(selectedTier.price).toFixed(2)}</strong> for {selectedTier.name}) before assigning to a cohort.
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Passing Score %</label>
                  <input
                    type="number"
                    min={40}
                    max={100}
                    value={assignPassingScore}
                    onChange={(e) => setAssignPassingScore(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-mono focus:bg-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={assignDueDate}
                    onChange={(e) => setAssignDueDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-xs focus:bg-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="mandatoryToggle"
                  checked={assignMandatory}
                  onChange={(e) => setAssignMandatory(e.target.checked)}
                  className="rounded text-indigo-600 bg-white border-slate-300 focus:ring-indigo-500"
                />
                <label htmlFor="mandatoryToggle" className="font-semibold text-slate-700 cursor-pointer">
                  Enforce as Mandatory Requirement
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingAssignment}
                  className={`px-5 py-2 text-white font-bold rounded-xl shadow-sm transition ${
                    requiresCohortPayment
                      ? "bg-amber-600 hover:bg-amber-700"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {creatingAssignment
                    ? "Assigning..."
                    : requiresCohortPayment
                    ? `Proceed to Checkout ($${Number(selectedTier?.price || 0).toFixed(2)})`
                    : "Assign Curriculum"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: ADD SKILL EXPECTATION                                  */}
      {/* ------------------------------------------------------------- */}
      {showSkillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Target className="w-4 h-4 text-indigo-600" /> Add Skill Target
              </h3>
              <button onClick={() => setShowSkillModal(false)} className="text-slate-400 hover:text-slate-700">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateSkill} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Skill Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Prompt Engineering, SQL Performance"
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Proficiency Level</label>
                <select
                  value={newSkillProficiency}
                  onChange={(e) => setNewSkillProficiency(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="Novice">Novice</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Department</label>
                <select
                  value={newSkillDept}
                  onChange={(e) => setNewSkillDept(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="All">All Departments (Company-wide)</option>
                  {stats.departments?.map((dept: string) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowSkillModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingSkill}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm"
                >
                  {creatingSkill ? "Adding..." : "Add Expectation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
